import { categoryKey } from "@/lib/categoryKey";

/**
 * Ganti nama kategori yang ikut memperbarui produk — sisi klien dari RPC
 * `admin_rename_category`. Semua helper di sini murni (tanpa jaringan) supaya
 * bisa diuji; pemanggilan RPC-nya ada di `renameAdminCategoryInSupabase`.
 *
 * Teks layar dipakai halaman admin saja dan ditulis tanpa kata ganti.
 */

/** Balikan RPC (jsonb) yang sudah dikoersi. Kunci yang tak dikirim = 0 / false. */
export type CategoryRenameResult = {
  dryRun: boolean;
  /** true = semua tuntas (nama master sudah diganti). false = masih ada batch. */
  done: boolean;
  /** Nama master benar-benar berubah (huruf besar/kecil pun dihitung). */
  renamed: boolean;
  from: string;
  name: string;
  /** Produk yang cocok sebelum panggilan ini menulis. */
  productsTotal: number;
  productsUpdated: number;
  productsRemaining: number;
  /** Dry-run: target (poin/rak/promo/voucher) yang akan ikut berubah. */
  targetsTotal: number;
  targetsUpdated: number;
  /** Dry-run: 1 bila kunci peta TikTok akan dipindah. */
  mapsTotal: number;
  mapsMoved: number;
  /** Kunci baru sudah punya peta TikTok: yang baru menang, yang lama dibiarkan. */
  mapsConflict: boolean;
};

function int(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** Koersi defensif atas jsonb balikan `admin_rename_category`. */
export function mapCategoryRenameResult(raw: unknown): CategoryRenameResult {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    dryRun: r.dry_run === true,
    done: r.done === true,
    renamed: r.renamed === true,
    from: typeof r.from === "string" ? r.from : "",
    name: typeof r.name === "string" ? r.name : "",
    productsTotal: int(r.products_total),
    productsUpdated: int(r.products_updated),
    productsRemaining: int(r.products_remaining),
    targetsTotal: int(r.targets_total),
    targetsUpdated: int(r.targets_updated),
    mapsTotal: int(r.maps_total),
    mapsMoved: int(r.maps_moved),
    mapsConflict: r.maps_conflict === true
  };
}

/**
 * Apakah nama BERUBAH (huruf besar/kecil pun dihitung, spasi tepi tidak)?
 * Menentukan perlu-tidaknya memanggil RPC ganti nama.
 */
export function categoryNameChanged(nextName: string, savedName: string): boolean {
  return (nextName ?? "").trim() !== (savedName ?? "").trim();
}

/**
 * Dua nama menunjuk kategori yang sama menurut `categoryKey` (beda huruf/spasi
 * saja). Dipakai memperbarui salinan produk di layar sesudah rename.
 */
export function sameCategory(a: string | null | undefined, b: string | null | undefined): boolean {
  return categoryKey(a) !== "" && categoryKey(a) === categoryKey(b);
}

const nf = new Intl.NumberFormat("id-ID");

/** "1.340" — bilangan bulat gaya Indonesia. */
export function formatCount(n: number): string {
  return nf.format(Math.max(0, Math.floor(Number.isFinite(n) ? n : 0)));
}

/** Hasil dry-run menuntut konfirmasi bila ada sesuatu yang ikut berubah. */
export function categoryRenameNeedsConfirm(preview: CategoryRenameResult): boolean {
  return preview.productsTotal > 0 || preview.targetsTotal > 0 || preview.mapsTotal > 0;
}

export type CategoryRenameConfirmText = {
  title: string;
  /** Satu kalimat per baris; layar menampilkannya sebagai paragraf terpisah. */
  lines: string[];
};

/** Isi panel konfirmasi sebelum simpan, dari hasil dry-run. */
export function categoryRenameConfirmText(
  preview: CategoryRenameResult,
  fromName: string,
  toName: string
): CategoryRenameConfirmText {
  const lines: string[] = [];
  if (preview.productsTotal > 0) {
    lines.push(
      `Kategori di ${formatCount(preview.productsTotal)} produk ikut diganti dari "${fromName}" ke "${toName}".`
    );
  }
  if (preview.targetsTotal > 0 || preview.mapsTotal > 0) {
    lines.push(
      "Rak beranda, program poin, promo, voucher, dan pemetaan TikTok yang memakai kategori ini ikut menyesuaikan."
    );
  }
  if (preview.mapsConflict) {
    lines.push(
      `Pemetaan TikTok untuk "${toName}" sudah ada, jadi pemetaan "${fromName}" tidak dipindah.`
    );
  }
  return {
    title:
      preview.productsTotal > 0
        ? `Nama baru juga dipakai di ${formatCount(preview.productsTotal)} produk`
        : "Nama baru juga dipakai di pengaturan lain",
    lines
  };
}

/** Pesan sukses simpan, menyebut dampak yang benar-benar terjadi. */
export function categoryRenameDoneMessage(result: CategoryRenameResult): string {
  if (result.productsUpdated > 0) {
    return `Kategori diperbarui. ${formatCount(result.productsUpdated)} produk ikut memakai nama baru.`;
  }
  return "Kategori diperbarui.";
}

/** Teks progres saat produk diperbarui per batch. */
export function categoryRenameProgressText(done: number, total: number): string {
  return `Memperbarui produk… ${formatCount(done)} dari ${formatCount(total)}`;
}

/** Penjelasan saat batch terputus di tengah jalan; mengulang simpan melanjutkannya. */
export function categoryRenameInterruptedMessage(done: number, total: number): string {
  return `Baru ${formatCount(done)} dari ${formatCount(total)} produk yang diperbarui. Tekan Simpan Perubahan lagi untuk melanjutkan.`;
}

/** Pesan bila pengecekan sebelum simpan gagal: simpan diblokir, bukan dilanjutkan tanpa konfirmasi. */
export const CATEGORY_RENAME_CHECK_FAILED =
  "Belum bisa mengecek produk yang memakai kategori ini. Coba lagi.";

/** Pesan bila RPC belum ada di database ini. Tidak mundur ke update biasa. */
export const CATEGORY_RENAME_UNAVAILABLE =
  "Ganti nama kategori belum aktif di database ini. Perubahan ikon tetap bisa disimpan.";

/** Jumlah putaran maksimal pemanggilan berulang (katup pengaman terhadap loop tak berujung). */
export const CATEGORY_RENAME_MAX_ROUNDS = 100;
